import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { BillingLineKind } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { SettlementInvoiceLines } from '~hooks/useSettlementLines/models';
import { Settlement, SettlementLine } from '~hooks/useSettlements/models';
import useSettlements from '~hooks/useSettlements/useSettlements';
import useSettlementLinesQuery from '~queries/useSettlementLinesQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import SettlementInvoiceEditDialog from './SettlementInvoiceEditDialog';
import SettlementLineRow from './SettlementLineRow';

interface SettlementLinesTabProps {
  onSettlementChange: (settlement: Settlement) => void;
  settlement: Settlement;
}

export default function SettlementLinesTab({
  onSettlementChange,
  settlement,
}: SettlementLinesTabProps) {
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { currencyFormatter } = useCompanyCurrency();
  const { getSettlementById, removeLinesFromSettlement } = useSettlements();
  const settlementId = settlement.id ?? '';
  const {
    data: loadedLines,
    isError: hasLoadError,
    isFetching: isLoading,
    refetch: reloadLines,
  } = useSettlementLinesQuery({
    companyId,
    reconcileState: settlement.reconcileState,
    settlementId,
  });
  const invoiceLines = loadedLines?.invoiceLines ?? [];
  const [lineToRemove, setLineToRemove] = useState<SettlementLine | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [invoiceToEdit, setInvoiceToEdit] = useState<SettlementInvoiceLines | null>(null);

  const lineCount = invoiceLines.reduce((total, lines) => total + lines.lineCount, 0);
  // taking out the only line leaves nothing to settle, so the API voids the settlement
  const isRemovingLastLine = lineCount === 1;

  const handleRemoveConfirm = async () => {
    if (!lineToRemove) {
      return;
    }

    setIsRemoving(true);
    try {
      const updatedSettlement = await removeLinesFromSettlement(
        companyId,
        settlementId,
        [lineToRemove],
        isRemovingLastLine,
      );
      onSettlementChange(updatedSettlement);
      toasterStore.push(
        alert(
          t(
            isRemovingLastLine
              ? 'approvals.driver_pay.settlements.lines.settlement_voided'
              : 'approvals.driver_pay.settlements.lines.line_removed',
          ),
          AlertTypes.success,
        ),
      );
    } catch {
      // the hook has already reported the failure
    } finally {
      setIsRemoving(false);
      setLineToRemove(null);
      // also after a failure: a 409 means the settlement or line changed since it was read
      void reloadLines();
    }
  };

  const handleEditClose = async () => {
    setInvoiceToEdit(null);
    void reloadLines();

    try {
      onSettlementChange(await getSettlementById(companyId, settlementId));
    } catch {
      // the hook has already reported the failure
    }
  };

  const isFirstLoad = isLoading && invoiceLines.length === 0;
  const canChangeLines = settlement.editable && !hasLoadError;

  if (isFirstLoad) {
    return (
      <Box sx={{ display: 'flex', gridColumn: '1 / -1', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        gridColumn: '1 / -1',
        opacity: isLoading ? 0.6 : 1,
      }}
    >
      {hasLoadError && (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, p: 2 }}>
          <Typography sx={{ color: 'error.main' }} variant="body2">
            {t('approvals.driver_pay.settlements.lines.read_failed')}
          </Typography>
          <SmallButton onClick={() => void reloadLines()} variant="outlined">
            {t('actions.retry')}
          </SmallButton>
        </Box>
      )}

      {invoiceLines.length === 0 && !hasLoadError && (
        <Typography sx={{ color: 'text.secondary', p: 2 }} variant="body2">
          {t('approvals.driver_pay.settlements.lines.empty')}
        </Typography>
      )}

      {invoiceLines.map((group) => {
        const canEditGroup = canChangeLines && group.hasEditableLine;
        const canRemove = (editable: boolean | null) =>
          canChangeLines && editable !== false;

        return (
          <Box key={group.invoice.id} sx={{ mb: 2 }}>
            <Box
              sx={(theme) => ({
                alignItems: 'center',
                bgcolor: theme.semantic.surfaceSunken,
                display: 'flex',
                justifyContent: 'space-between',
                px: 1,
                py: 0.5,
              })}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {[group.invoice.job?.jobId, group.invoice.order?.orderId]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              {canEditGroup && (
                <SmallButton
                  color="brandV2Yellow"
                  onClick={() => setInvoiceToEdit(group)}
                  startIcon={<EditOutlinedIcon />}
                >
                  {t('approvals.driver_pay.settlements.lines.edit')}
                </SmallButton>
              )}
            </Box>

            {group.lineItems.map((lineItem) => (
              <SettlementLineRow
                key={lineItem.id}
                currencyFormatter={currencyFormatter}
                name={lineItem.name ?? ''}
                onRemove={
                  canRemove(lineItem.editable)
                    ? () =>
                        setLineToRemove({
                          id: lineItem.id,
                          kind: BillingLineKind.INVOICE_LINE_ITEM,
                        })
                    : null
                }
                quantity={lineItem.quantity}
                rate={lineItem.rate}
                totalAmount={lineItem.totalAmount}
                wasRate={lineItem.hasRateVariance ? lineItem.derivedRate : null}
                wasTotalAmount={null}
              />
            ))}
            {group.addOnCharges.map((addOnCharge) => {
              const addOnChargeId = addOnCharge.id;

              return (
                <SettlementLineRow
                  key={addOnChargeId}
                  currencyFormatter={currencyFormatter}
                  name={addOnCharge.name}
                  onRemove={
                    addOnChargeId && canRemove(addOnCharge.editable)
                      ? () =>
                          setLineToRemove({
                            id: addOnChargeId,
                            kind: BillingLineKind.ADD_ON_CHARGE,
                          })
                      : null
                  }
                  quantity={addOnCharge.quantity}
                  rate={addOnCharge.rate}
                  totalAmount={addOnCharge.totalAmount}
                  wasRate={null}
                  wasTotalAmount={
                    addOnCharge.hasTotalVariance ? addOnCharge.derivedTotalAmount : null
                  }
                />
              );
            })}
          </Box>
        );
      })}

      <ConfirmationDialog
        confirmButtonProps={{
          children: t(
            isRemovingLastLine
              ? 'approvals.driver_pay.settlements.lines.void_confirm'
              : 'approvals.driver_pay.settlements.lines.remove_confirm',
          ),
        }}
        isBusy={isRemoving}
        isOpen={lineToRemove !== null}
        onClose={() => {
          if (!isRemoving) {
            setLineToRemove(null);
          }
        }}
        onConfirm={handleRemoveConfirm}
        title={t(
          isRemovingLastLine
            ? 'approvals.driver_pay.settlements.lines.void_title'
            : 'approvals.driver_pay.settlements.lines.remove_title',
        )}
      >
        {t(
          isRemovingLastLine
            ? 'approvals.driver_pay.settlements.lines.void_description'
            : 'approvals.driver_pay.settlements.lines.remove_description',
        )}
      </ConfirmationDialog>

      {invoiceToEdit && (
        <SettlementInvoiceEditDialog
          invoice={invoiceToEdit.invoice}
          onClose={handleEditClose}
        />
      )}
    </Box>
  );
}
